import { ChangeDetectionStrategy, Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import {
   AbstractControl,
   UntypedFormBuilder,
   UntypedFormControl,
   UntypedFormGroup,
   ValidationErrors,
   ValidatorFn,
} from '@angular/forms';
import {
   TimeRangeParameterFormModel,
   TimeRangeReportParameterValue,
} from '@ndt/report-output/shared/models/time-range-report-parameter-value';
import { Subject } from 'rxjs';
import { ReportParameterConfig } from '../../report-parameter-list/report-parameter.config';

@Component({
   encapsulation: ViewEncapsulation.None,
   changeDetection: ChangeDetectionStrategy.OnPush,
   selector: 'time-range-report-parameter',
   templateUrl: './time-range-report-parameter.component.html',
   styleUrls: ['./time-range-report-parameter.component.scss'],
   standalone: false,
})
export class TimeRangeReportParameterComponent implements OnDestroy {
   config: ReportParameterConfig;

   set form(form: UntypedFormGroup) {
      form.setValidators(this.timeRangeValidator());
      this._form = form;
      const formValue: TimeRangeParameterFormModel = this.form.value;
      if (formValue) this.initializeContentFormValue(formValue);
   }

   get form(): UntypedFormGroup {
      return this._form;
   }

   private _form: UntypedFormGroup;

   constructor(private formBuilder: UntypedFormBuilder) {}

   private destroyed$ = new Subject<void>();

   ngOnDestroy(): void {
      // QMG-3199 - Once updated to angular v12+ we can remove this code to handle cleaning up form controls
      // https://github.com/angular/angular/issues/27803
      const formValue: TimeRangeParameterFormModel = this.form.value;

      this.form.removeControl('startTime');
      this.form.removeControl('endTime');

      const timeRangeReportParameterValue: TimeRangeReportParameterValue = {
         endTime: formValue.endTime,
         sortOrder: formValue.sortOrder,
         startTime: formValue.startTime,
      };

      const startTime: UntypedFormControl = this.formBuilder.control(timeRangeReportParameterValue.startTime);
      const endTime: UntypedFormControl = this.formBuilder.control(timeRangeReportParameterValue.endTime);

      this.form.addControl('startTime', startTime);
      this.form.addControl('endTime', endTime);
      // -------------------------------------------------------------------------------------------------
      this.destroyed$.next();
   }

   private initializeContentFormValue(formValue: TimeRangeReportParameterValue): void {
      const formatTime = (value: Date | string): string => {
         if (!value) return '00:00';

         if (typeof value === 'string' && /^\d{2}:\d{2}$/.test(value)) {
            return value;
         }

         const d = new Date(value);
         const hours = d.getHours().toString().padStart(2, '0');
         const minutes = d.getMinutes().toString().padStart(2, '0');
         return `${hours}:${minutes}`;
      };

      this.form.setValue({
         ...formValue,
         startTime: formatTime(formValue.startTime || ''),
         endTime: formatTime(formValue.endTime || ''),
      });
   }
   private timeRangeValidator(): ValidatorFn {
      return (form: AbstractControl): ValidationErrors | null => {
         const isStartBeforeEnd = form.get('startTime')?.value <= form.get('endTime')?.value;
         const endTime = parseInt(form.get('endTime')?.value.split(':').join('')) ?? 0;
         const startTime = parseInt(form.get('startTime')?.value.split(':').join('')) ?? 0;
         const timeDifferance = endTime - startTime;

         return !isStartBeforeEnd || timeDifferance < 100 ? { startBeforeEndError: true } : null;
      };
   }
}
